import type { QueryClient } from '@tanstack/react-query';
import { isProjectAccessDeniedError } from '../hooks/queries/projectAccessQuery';
import { queryKeys } from '../hooks/queries/queryKeys';
import type { ProjectAccess } from '../lib/api/projectPermissions';

export const isProjectQuery = (key: readonly unknown[], projectId: string): boolean =>
  key[1] === projectId || (key[0] === 'projects' && key[1] === 'detail' && key[2] === projectId);

const isProjectList = (key: readonly unknown[]): boolean =>
  key[0] === 'projects' && (key.length === 2 || key[2] === 'archived' || key[2] === 'shareable');

// 홈은 데스크탑 사이드바의 데이터 공급자라 숨겨진 탭에서도 목록을 갱신한다.
// projectId가 없는 호출은 연결 복구 신호이며, 캐시 전체가 아니라 기준정보·접근권한·상태 배지를 재검증한다.
export const invalidateProjectMetadata = async (
  client: QueryClient,
  projectId?: string,
  refetchType: 'active' | 'none' = 'active',
): Promise<void> => {
  const accessQueries = client.getQueryCache().findAll({
    predicate: ({ queryKey }) =>
      queryKey[0] === 'projects' && queryKey[2] === 'access' && (projectId === undefined || queryKey[1] === projectId),
  });
  await Promise.all([
    client.invalidateQueries({ queryKey: queryKeys.me.projects }),
    client.invalidateQueries({ queryKey: queryKeys.me.feedAll, refetchType }),
    client.invalidateQueries({
      queryKey: projectId ? queryKeys.navigationBadges.project(projectId) : queryKeys.navigationBadges.all,
      refetchType,
    }),
    client.invalidateQueries({ predicate: ({ queryKey }) => isProjectList(queryKey), refetchType }),
    client.invalidateQueries({
      refetchType,
      predicate: ({ queryKey }) =>
        queryKey[0] === 'projects' &&
        (queryKey[1] === 'detail' || queryKey[2] === 'access') &&
        (projectId === undefined || isProjectQuery(queryKey, projectId)),
    }),
    ...(projectId
      ? [client.invalidateQueries({ queryKey: queryKeys.members.projectMembers(projectId), refetchType })]
      : []),
  ]);
  for (const query of accessQueries) {
    const state = query.state;
    const data = state.data as ProjectAccess | undefined;
    // 접근 상실 판정은 `isProjectAccessDeniedError`가 SSOT다. 훅(`useProjectPermissions`)과
    // 규칙이 갈리면 한쪽만 일시 장애를 권한 상실로 읽어 화면과 캐시가 서로 다르게 움직인다.
    const lostAccess =
      isProjectAccessDeniedError(state.error) ||
      (state.status === 'success' && data !== undefined && !data.permissions.includes('project.enter'));
    if (!lostAccess || state.fetchStatus !== 'idle') continue;
    const id = query.queryKey[1] as string;
    // 거부된 access 상태는 남겨 게이트가 재마운트되더라도 이전 데이터로 돌아가지 않게 한다.
    await client.cancelQueries({
      predicate: ({ queryKey }) => isProjectQuery(queryKey, id) && queryKey[2] !== 'access',
    });
    client.removeQueries({ predicate: ({ queryKey }) => isProjectQuery(queryKey, id) && queryKey[2] !== 'access' });
  }
};
